/**
 * ReloadUI.js - Provides visual feedback for weapon reloading
 * This module attaches to the game without modifying existing code
 */

class ReloadUI {
  constructor() {
    // Base configuration that can be modified by XP/leveling
    this.config = {
      baseReloadTime: 2000, // Default 2 seconds (reduced from 4)
      reloadTimeModifier: 1.0, // Multiplier for XP/leveling effects
    };

    // Create UI elements
    this.createElements();
    
    // Store state
    this.isReloading = false;
    this.reloadStartTime = 0;
    this.reloadDuration = 0;
    this.animationFrameId = null;
    this.currentWeapon = null;
    this.reloadStarted = false;
    this.initializationAttempts = 0;
    
    // Bind methods
    this.checkForReload = this.checkForReload.bind(this);
    this.updateReloadProgress = this.updateReloadProgress.bind(this);
    this.startMonitoring = this.startMonitoring.bind(this);
    this.stopMonitoring = this.stopMonitoring.bind(this);
    this.waitForPlayerController = this.waitForPlayerController.bind(this);
    
    // Start waiting for playerController to be available
    if (document.readyState === 'complete') {
      this.waitForPlayerController();
    } else {
      window.addEventListener('load', this.waitForPlayerController);
    }
    
    console.log('ReloadUI initialized');
  }
  
  /**
   * Wait for playerController to be available before starting monitoring
   */
  waitForPlayerController() {
    // If we're already showing reload UI successfully, don't keep retrying
    if (this.isReloading || document.getElementById('reload-ui')) {
      console.log('ReloadUI is functioning, stopping playerController checks');
      return;
    }
    
    // Check if playerController exists
    if (window.playerController) {
      console.log('ReloadUI: playerController found, starting monitoring');
      this.startMonitoring();
      return;
    }
    
    // Count attempts to limit retries
    this.initializationAttempts++;
    
    if (this.initializationAttempts > 50) {
      console.warn('ReloadUI: Too many attempts to find playerController, giving up');
      return;
    }
    
    // Try again after a delay with exponential backoff
    const delay = Math.min(3000, 200 * Math.pow(1.2, this.initializationAttempts));
    setTimeout(this.waitForPlayerController, delay);
  }
  
  /**
   * Create UI elements for reload indicator
   */
  createElements() {
    // Create container element
    this.container = document.createElement('div');
    this.container.id = 'reload-ui';
    this.container.style.cssText = `
      position: fixed;
      top: 52%;
      left: 50%;
      transform: translate(-50%, 0);
      width: 75px;
      padding: 5px;
      font-family: Arial, sans-serif;
      font-size: 9px;
      font-weight: bold;
      text-align: center;
      color: #ffffff;
      text-shadow: 1px 1px 1px #000000;
      background-color: rgba(0, 0, 0, 0.6);
      border: 1px solid #777777;
      border-radius: 4px;
      pointer-events: none;
      z-index: 9999;
      opacity: 0;
    `;
    
    // Create text element
    this.textElement = document.createElement('div');
    this.textElement.textContent = 'RELOADING';
    this.container.appendChild(this.textElement);
    
    // Create progress bar container
    this.progressContainer = document.createElement('div');
    this.progressContainer.style.cssText = `
      width: 100%;
      height: 6px;
      background-color: rgba(50, 50, 50, 0.6);
      border-radius: 3px;
      margin-top: 4px;
      overflow: hidden;
      border: 1px solid #999999;
    `;
    
    // Create progress bar
    this.progressBar = document.createElement('div');
    this.progressBar.style.cssText = `
      width: 0%;
      height: 100%;
      background-color: #4a90e2;
    `;
    
    this.progressContainer.appendChild(this.progressBar);
    this.container.appendChild(this.progressContainer);
    
    // Add to document body
    document.body.appendChild(this.container);
    
    // Add debug logging
    console.log('ReloadUI elements created and added to document:', 
      'Container exists:', !!this.container,
      'In document:', document.getElementById('reload-ui') != null,
      'Z-index:', this.container.style.zIndex);
  }
  
  /**
   * Start monitoring for reload events
   */
  startMonitoring() {
    // Double-check playerController exists before starting
    if (!window.playerController) {
      console.error('ReloadUI: Cannot start monitoring, playerController still not available');
      return;
    }
    
    // Start checking for reload status
    this.checkInterval = setInterval(this.checkForReload, 100);
    console.log('ReloadUI monitoring started');
  }
  
  /**
   * Stop monitoring for reload events
   */
  stopMonitoring() {
    clearInterval(this.checkInterval);
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    
    this.hideReloadUI();
    console.log('ReloadUI monitoring stopped');
  }
  
  /**
   * Check if the player's weapon is currently reloading
   */
  checkForReload() {
    // Safety check - make sure we have a player controller
    if (!window.playerController) {
      console.log('ReloadUI: playerController not found');
      return;
    }
    
    // Get the current equipped item
    const inventory = window.playerController.inventory;
    if (!inventory) {
      console.log('ReloadUI: inventory not found');
      return;
    }
    
    const equippedItem = inventory.getEquipped();
    
    // Make sure we have a weapon to check
    if (!equippedItem || !equippedItem.cooldown) {
      // If we were reloading before but not now, hide the UI
      if (this.isReloading) {
        this.hideReloadUI();
      }
      return;
    }
    
    // Check if this is Pistol by looking for required properties
    if (typeof equippedItem.shotsFired !== 'number' || 
        typeof equippedItem.shotsBeforeCooldown !== 'number') {
      return;
    }
    
    // Detect reload state by checking for isReloading flag OR
    // if shotsFired is reset to 0 from a previous max value
    const isCurrentlyReloading = equippedItem.isReloading ||
        (this.previousShotsFired === equippedItem.shotsBeforeCooldown && 
         equippedItem.shotsFired === 0);
    
    // Store shotsFired for next comparison
    this.previousShotsFired = equippedItem.shotsFired;
    
    if (isCurrentlyReloading && !this.isReloading) {
      // New reload starting - use the weapon's actual cooldown time
      this.startReloadAnimation(equippedItem);
    } else if (!isCurrentlyReloading && this.isReloading) {
      // Reload ended
      this.hideReloadUI();
    }
  }
  
  /**
   * Get the current effective reload time based on base time and modifiers
   * @returns {number} The current reload time in milliseconds
   */
  getEffectiveReloadTime() {
    return this.config.baseReloadTime * this.config.reloadTimeModifier;
  }

  /**
   * Update reload time modifier (for use with XP/leveling system)
   * @param {number} modifier - Multiplier for reload time (0.5 = twice as fast)
   */
  setReloadTimeModifier(modifier) {
    this.config.reloadTimeModifier = modifier;
    console.log(`Reload time modifier updated: ${modifier}x`);
  }

  /**
   * Start showing the reload animation
   * @param {Object} weapon - The weapon that's reloading
   */
  startReloadAnimation(weapon) {
    if (!weapon) return;
    
    this.isReloading = true;
    this.currentWeapon = weapon;
    
    // Always use the weapon's cooldown time, modified by any reload speed bonuses
    this.reloadDuration = weapon.cooldown * this.config.reloadTimeModifier;
    this.reloadStartTime = performance.now();
    
    // Reset progress bar
    this.progressBar.style.width = '0%';
    
    // Show UI with full opacity
    this.container.style.opacity = '1';
    
    // Start animation loop
    this.updateReloadProgress();
    
    console.log(`Reload animation started (${this.reloadDuration}ms)`);
  }
  
  /**
   * Update the reload progress bar based on time
   */
  updateReloadProgress() {
    if (!this.isReloading) return;
    
    const currentTime = performance.now();
    const elapsed = currentTime - this.reloadStartTime;
    const progress = Math.min(elapsed / this.reloadDuration, 1);
    
    // Update progress bar
    this.progressBar.style.width = `${progress * 100}%`;
    
    // Check if reload is complete
    if (progress >= 1) {
      // Delay hiding UI by a small amount to ensure it's visible until reload is truly done
      setTimeout(() => {
        this.hideReloadUI();
      }, 300);
    } else {
      // Continue animation
      this.animationFrameId = requestAnimationFrame(this.updateReloadProgress);
    }
  }
  
  /**
   * Hide the reload UI
   */
  hideReloadUI() {
    this.isReloading = false;
    this.container.style.opacity = '0';
    
    // Cancel animation
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    
    console.log('Reload UI hidden');
  }
}

// Create a global function to directly trigger reload UI for the pistol
window.showReloadUI = function(durationMs, directPistolReference) {
  // Always use 2 seconds (2000ms) for reload time
  const reloadTime = 2000;
  
  // Use directly passed pistol reference if available
  let pistol = directPistolReference;
  
  // Otherwise try to get it from playerController as fallback
  if (!pistol) {
    pistol = window.playerController?.inventory?.getEquipped();
  }
  
  if (pistol && window.reloadUI) {
    // Override the cooldown to ensure 2-second reload
    pistol.cooldown = reloadTime;
    window.reloadUI.startReloadAnimation(pistol);
  }
};

// Create a single instance when the script is loaded
const reloadUI = new ReloadUI();
window.reloadUI = reloadUI; // Make globally available

// Export for potential future integration
export default reloadUI; 